import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Receipt, Droplets, Fuel, Building2, MapPin, TrendingUp, TrendingDown, Save, FileSpreadsheet, FileDown } from "lucide-react";
import { saveBlob } from "../lib/download";
import { printWaeiAnalysis } from "../lib/waeiPdf";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { Money } from "./SaudiRiyal";

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: d });

const Kpi = ({ label, value, sub, tone = "slate", testid }) => (
  <div data-testid={testid} className={`rounded-2xl border p-4 ${tone === "emerald" ? "border-emerald-100 bg-emerald-50" : tone === "rose" ? "border-rose-100 bg-rose-50" : "border-slate-200 bg-white"}`}>
    <p className={`text-xs ${tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-700" : "text-slate-500"}`}>{label}</p>
    <p className={`font-heading text-xl font-bold font-mono-nums mt-1 ${tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-700" : "text-slate-800"}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const Bars = ({ title, icon: Icon, items, testid, valueKey = "amount" }) => {
  const max = Math.max(1, ...items.map((x) => x[valueKey] || 0));
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
      <h4 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5 mb-3"><Icon className="h-4 w-4 text-emerald-600" /> {title}</h4>
      {items.length === 0 ? <p className="text-xs text-slate-400">—</p> : (
        <div className="space-y-2.5">
          {items.map((x) => (
            <div key={x.name} className="text-xs">
              <div className="flex justify-between gap-2 mb-1">
                <span className="text-slate-600 truncate">{x.name}</span>
                <span className="font-mono-nums font-semibold text-slate-800 whitespace-nowrap">
                  {valueKey === "count" ? `${x.count} تعبئة · ` : ""}<Money value={x.amount} />
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${((x[valueKey] || 0) / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LimitEditor = ({ limit, onSaved, canEdit }) => {
  const [v, setV] = useState(limit);
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(limit), [limit]);
  const save = async () => {
    setBusy(true);
    try { await api.saveFuelSettings({ suspicious_liters: Number(v) }); toast.success("تم حفظ حدّ التعبئة المشبوهة"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600">
      <span>حدّ التعبئة المشبوهة (لتر):</span>
      <Input data-testid="waei-limit-input" type="number" min="1" value={v} disabled={!canEdit}
        onChange={(e) => setV(e.target.value)} className="h-8 w-20 text-center" />
      {canEdit && (
        <Button data-testid="waei-limit-save" size="sm" variant="outline" disabled={busy || !v} onClick={save} className="h-8 gap-1">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} حفظ
        </Button>
      )}
    </div>
  );
};

const PlatesTable = ({ plates }) => (
  <div data-testid="waei-top-plates" className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
    <h4 className="font-semibold text-slate-700 text-sm px-4 pt-4 pb-2">تكلفة كل لوحة ({plates.length})</h4>
    <div className="max-h-96 overflow-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 sticky top-0">
          <tr><th className="p-2 text-right">#</th><th className="p-2 text-right">اللوحة</th><th className="p-2 text-right">المندوب / السائق</th><th className="p-2">التعبئات</th><th className="p-2">اللترات</th><th className="p-2">المبلغ</th></tr>
        </thead>
        <tbody>
          {plates.map((p, i) => (
            <tr key={p.plate} data-testid={`waei-plate-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50/60">
              <td className="p-2 text-slate-400">{i + 1}</td>
              <td className="p-2 font-mono-nums text-slate-700" dir="ltr">{p.plate}</td>
              <td className="p-2 text-slate-700">{p.employee_name}{!p.matched && <span className="mr-1 rounded bg-amber-50 text-amber-600 px-1 text-[10px]">غير مربوط</span>}</td>
              <td className="p-2 text-center font-mono-nums">{p.count || "—"}</td>
              <td className="p-2 text-center font-mono-nums">{fmt(p.liters)}</td>
              <td className="p-2 text-center font-mono-nums font-bold text-emerald-700"><Money value={p.amount} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SuspiciousList = ({ data }) => (
  <div data-testid="waei-suspicious" className="rounded-2xl border border-rose-200 bg-white overflow-hidden">
    <h4 className="font-semibold text-rose-700 text-sm px-4 pt-4 pb-2 flex items-center gap-1.5">
      <AlertTriangle className="h-4 w-4" /> تعبئات تتجاوز {fmt(data.limit, 0)} لتر — {data.suspicious_count} تعبئة · <Money value={data.suspicious_amount} />
    </h4>
    {data.tx_count === 0 ? (
      <p className="px-4 pb-4 text-xs text-slate-400">يحتاج هذا التحليل ملف «مصنف باللوحة» لأنه يحتوي كل تعبئة على حدة.</p>
    ) : data.suspicious.length === 0 ? (
      <p className="px-4 pb-4 text-xs text-slate-400">لا توجد تعبئات مشبوهة لهذا الشهر.</p>
    ) : (
      <div className="max-h-80 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-rose-50 text-rose-700 sticky top-0">
            <tr><th className="p-2 text-right">اللوحة</th><th className="p-2 text-right">المندوب</th><th className="p-2">اللترات</th><th className="p-2">المبلغ</th><th className="p-2 text-right">المحطة</th></tr>
          </thead>
          <tbody>
            {data.suspicious.map((s, i) => (
              <tr key={i} data-testid={`waei-susp-row-${i}`} className="border-t border-slate-100">
                <td className="p-2 font-mono-nums" dir="ltr">{s.plate}</td>
                <td className="p-2">{s.employee_name}</td>
                <td className="p-2 text-center font-mono-nums font-bold text-rose-700">{fmt(s.liters)}</td>
                <td className="p-2 text-center font-mono-nums"><Money value={s.amount} /></td>
                <td className="p-2 text-slate-500">{s.station}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const WaeiAnalysisPanel = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const load = useCallback(async (m) => {
    try { setData(await api.waeiAnalysis(m || undefined)); }
    catch { toast.error("تعذّر تحميل تحليل واعي"); }
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  if (!data) return <div className="py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  if (!data.month) return <p data-testid="waei-empty" className="py-10 text-center text-slate-400 text-sm">لا توجد بيانات «واعي» بعد — ارفع تقرير الدريس من تبويب تكلفة الوقود باختيار «واعي».</p>;
  const diff = data.previous ? data.total - data.previous.total : null;

  return (
    <div className="space-y-4" data-testid="waei-analysis">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-600" /> تحليل فاتورة واعي (الدريس)</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <LimitEditor limit={data.limit} canEdit={canEdit} onSaved={() => load(month)} />
          <Button data-testid="waei-export-excel" size="sm" variant="outline" className="h-9 gap-1.5" onClick={async () => {
            try { await saveBlob(await api.waeiExport(data.month), `waei_${data.month}.xlsx`); } catch { toast.error("تعذّر التصدير"); }
          }}><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel</Button>
          <Button data-testid="waei-export-pdf" size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => printWaeiAnalysis(data)}>
            <FileDown className="h-4 w-4 text-rose-600" /> PDF
          </Button>
          <select data-testid="waei-month-select" value={data.month} onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
            {data.months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="waei-kpi-total" tone="emerald" label="الإجمالي شامل الضريبة" value={<Money value={data.total} />}
          sub={diff !== null ? <span className="inline-flex items-center gap-1">{diff >= 0 ? <TrendingUp className="h-3 w-3 text-rose-500" /> : <TrendingDown className="h-3 w-3 text-emerald-500" />}{fmt(Math.abs(diff))} عن {data.previous.month}</span> : null} />
        <Kpi testid="waei-kpi-net" label="قبل الضريبة" value={<Money value={data.net} />} />
        <Kpi testid="waei-kpi-vat" label="ضريبة القيمة المضافة 15%" value={<Money value={data.vat} />} />
        <Kpi testid="waei-kpi-liters" label="إجمالي اللترات" value={fmt(data.liters)} sub={`سعر اللتر ${fmt(data.price_per_liter, 3)} ⃁`} />
        <Kpi testid="waei-kpi-plates" label="عدد اللوحات" value={data.plates_count} sub={data.unmatched ? `${data.unmatched} غير مربوطة بمندوب` : "كلها مربوطة"} />
        <Kpi testid="waei-kpi-tx" label="عدد التعبئات" value={data.tx_count || "—"} sub={data.avg_tx ? `متوسط التعبئة ${fmt(data.avg_tx)} ⃁` : "غير متاح في هذه الصيغة"} />
        <Kpi testid="waei-kpi-avg-plate" label="متوسط تكلفة اللوحة" value={<Money value={data.plates_count ? data.total / data.plates_count : 0} />} />
        <Kpi testid="waei-kpi-susp" tone="rose" label="تعبئات مشبوهة" value={data.suspicious_count} sub={<>بمبلغ <Money value={data.suspicious_amount} /></>} />
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Bars testid="waei-fuel-types" title="حسب نوع الوقود" icon={Fuel} items={data.fuel_types} />
        <Bars testid="waei-departments" title="حسب الإدارة / القسم" icon={Building2} items={data.departments} />
        <Bars testid="waei-stations" title="أكثر المحطات تعبئة" icon={MapPin} items={data.stations} valueKey="count" />
      </div>

      <SuspiciousList data={data} />
      <PlatesTable plates={data.top_plates} />
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><Droplets className="h-3 w-3" /> رفع ملف جديد لنفس الشهر يستبدل أرقام نفس اللوحات تلقائياً — لا تتكرر المبالغ عند رفع «مصنف بالوقود» و«مصنف باللوحة» معاً.</p>
    </div>
  );
};
